import React from 'react';
import { motion } from 'framer-motion';
import { IconWrapper } from '../../components/common/IconWrapper';
import {
  HiAcademicCap,
  HiChartBar,
  HiUsers,
  HiDevicePhoneMobile,
  HiCursorArrowRays,
  HiDocumentChartBar,
  HiSparkles,
  HiBolt,
  HiArrowRight,
  HiGlobeAlt,
  HiShieldCheck,
  HiBookOpen,
  HiVideoCamera
} from 'react-icons/hi2';

const growthMetrics = [
  { label: 'Student Engagement', value: '+45%', description: 'Average increase in active participation and course completion rates.' },
  { label: 'Learning Outcomes', value: '+32%', description: 'Improved test scores and knowledge retention through personalized learning paths.' },
  { label: 'Platform Adoption', value: '85%', description: 'Faculty and student satisfaction with digital learning tools.' }
];

const capabilityPillars = [
  {
    title: 'Learning Management Systems',
    description: 'Modern LMS platforms with course management, content delivery, and assessment tools.',
    bullets: ['Course authoring & content management', 'Real-time progress tracking & analytics', 'Gamification & interactive learning'],
    icon: HiBookOpen
  },
  {
    title: 'Student Experience Platforms',
    description: 'Engaging web and mobile applications for seamless learning experiences across devices.',
    bullets: ['Responsive web & mobile apps', 'Video streaming & virtual classrooms', 'Collaborative tools & discussion forums'],
    icon: HiDevicePhoneMobile
  },
  {
    title: 'Analytics & Insights',
    description: 'Data-driven insights to improve learning outcomes and optimize educational programs.',
    bullets: ['Student performance dashboards', 'Learning analytics & predictive models', 'Administrative reporting & compliance'],
    icon: HiChartBar
  }
];

const initiatives = [
  {
    title: 'Personalized Learning Paths',
    description: 'AI-driven recommendations that adapt to each student\'s learning style and pace.',
    icon: HiUsers
  },
  {
    title: 'Virtual Classrooms',
    description: 'Interactive online learning environments with live sessions, recordings, and collaboration tools.',
    icon: HiVideoCamera
  },
  {
    title: 'Assessment & Grading',
    description: 'Automated testing, grading, and feedback systems that save time and improve accuracy.',
    icon: HiDocumentChartBar
  },
  {
    title: 'Administrative Automation',
    description: 'Streamlined enrollment, scheduling, and student information management systems.',
    icon: HiCursorArrowRays
  }
];

const techLogos = [
  'Learning Management Systems (Canvas, Moodle, Blackboard)',
  'Video Platforms (Zoom, Microsoft Teams, Kaltura)',
  'Content Management (Drupal, WordPress, Contentful)',
  'Analytics & BI (Power BI, Tableau, Google Analytics)',
  'Cloud Platforms (AWS, Azure, GCP)',
  'Student Information Systems (Banner, PeopleSoft, Workday)'
];

const caseStudy = {
  title: 'Leading Online Education Platform',
  description:
    'Built a comprehensive learning platform serving 50,000+ students with personalized courses, virtual classrooms, and advanced analytics. Student engagement increased by 45%, course completion rates improved by 38%, and administrative efficiency grew by 60%.',
  highlights: [
    { metric: 'Student Engagement', value: '+45%' },
    { metric: 'Course Completion', value: '+38%' },
    { metric: 'Admin Efficiency', value: '+60%' },
    { metric: 'Platform Uptime', value: '99.9%' }
  ]
};

const heroImage = 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1600&q=80';

export const EducationSolutionPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* Hero Banner Section */}
      <section className="relative isolate overflow-hidden pt-36 pb-32">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Education"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/65 via-slate-950/55 to-slate-950/65" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white drop-shadow-[0_4px_20px_rgba(15,23,42,0.4)]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="inline-flex items-center px-5 py-2 rounded-full bg-blue-50/20 dark:bg-blue-900/30 border border-blue-100/30 dark:border-blue-900/60 mb-6 backdrop-blur-sm"
          >
            <IconWrapper icon={HiAcademicCap} className="w-5 h-5 text-blue-300 mr-2" />
            <span className="text-sm font-semibold text-white">Education</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
          >
            Transform Learning Experiences
            <span className="block text-blue-400">
              With Digital Platforms
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg md:text-xl text-white/85 leading-relaxed max-w-3xl mx-auto"
          >
            Build modern learning management systems, student portals, and analytics platforms that enhance engagement, improve outcomes, and streamline educational operations.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-wrap gap-4 justify-center mt-8"
          >
            <motion.button
              className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-slate-800 text-white font-semibold rounded-2xl shadow-lg overflow-hidden"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="relative z-10 flex items-center">
                Talk to education experts
                <IconWrapper icon={HiArrowRight} className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-slate-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.button>
            <motion.button
              className="px-8 py-4 border border-white/30 text-white rounded-2xl font-semibold bg-white/10 dark:bg-transparent backdrop-blur-sm hover:bg-white/20 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Download capability deck
            </motion.button>
          </motion.div>
        </div>
      </section>

      <section className="pt-20 pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="inline-flex items-center px-5 py-2 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-900/60 mb-6">
              <IconWrapper icon={HiAcademicCap} className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-sm font-semibold text-blue-600 dark:text-blue-300">Education</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white mb-6 leading-tight">
              Transform Learning Experiences with Digital Platforms
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed mb-8">
              Build modern learning management systems, student portals, and analytics platforms that enhance engagement,
              improve outcomes, and streamline educational operations.
            </p>
            <motion.div className="flex flex-wrap gap-4" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
              <motion.button
                className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-slate-800 text-white font-semibold rounded-2xl shadow-lg overflow-hidden"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="relative z-10 flex items-center">
                  Talk to education experts
                  <IconWrapper icon={HiArrowRight} className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-slate-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.button>
              <motion.button
                className="px-8 py-4 border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-200 rounded-2xl font-semibold bg-white/70 dark:bg-transparent backdrop-blur-sm"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Download capability deck
              </motion.button>
            </motion.div>
          </motion.div>

          <motion.div
            className="bg-white dark:bg-slate-950 rounded-3xl border border-slate-200 dark:border-slate-800 p-8 shadow-xl"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid sm:grid-cols-2 gap-5">
              {initiatives.map(item => (
                <div key={item.title} className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center mb-3">
                    <IconWrapper icon={item.icon} className="w-5 h-5 text-blue-600 dark:text-blue-200" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{item.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-3 gap-6">
          {growthMetrics.map(stat => (
            <motion.div
              key={stat.label}
              className="p-6 rounded-2xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <p className="text-4xl font-extrabold text-blue-600 mb-1">{stat.value}</p>
              <p className="text-sm uppercase tracking-wide text-slate-500">{stat.label}</p>
              <p className="text-sm mt-3 text-slate-600 dark:text-slate-300">{stat.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">What we build</h2>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-900/50 text-sm font-semibold text-blue-600 dark:text-blue-300">
              <IconWrapper icon={HiSparkles} className="w-4 h-4 mr-2 text-blue-500" />
              From blueprint to rollout
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {capabilityPillars.map(pillar => (
              <motion.div
                key={pillar.title}
                className="p-6 rounded-3xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-lg"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="w-12 h-12 rounded-2xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center mb-4">
                  <IconWrapper icon={pillar.icon} className="w-6 h-6 text-blue-600 dark:text-blue-200" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">{pillar.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">{pillar.description}</p>
                <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  {pillar.bullets.map(item => (
                    <li key={item} className="flex items-start gap-2">
                      <IconWrapper icon={HiShieldCheck} className="w-4 h-4 text-blue-500 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6">Ecosystem & accelerators</h2>
          <p className="text-slate-600 dark:text-slate-300 mb-10">We integrate with your existing educational technology stack or provide custom solutions.</p>
          <div className="flex flex-wrap gap-3">
            {techLogos.map(tech => (
              <motion.span
                key={tech}
                className="px-4 py-2 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm font-medium text-slate-700 dark:text-slate-200 shadow-sm"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.2 }}
              >
                {tech}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[60%_40%] gap-10 items-center">
            <motion.div
              className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-blue-600 via-slate-700 to-slate-900 text-white p-10 shadow-2xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-3xl font-bold mb-4">{caseStudy.title}</h3>
              <p className="text-lg text-white/90 mb-8">{caseStudy.description}</p>
              <div className="grid grid-cols-2 gap-6">
                {caseStudy.highlights.map(highlight => (
                  <div key={highlight.metric}>
                    <p className="text-3xl font-extrabold">{highlight.value}</p>
                    <p className="text-sm uppercase tracking-wider text-white/80">{highlight.metric}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              className="p-8 rounded-3xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-xl h-full flex flex-col justify-between"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div>
                <h4 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">Why educational institutions partner with us</h4>
                <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
                  <li className="flex gap-2">
                    <IconWrapper icon={HiSparkles} className="w-4 h-4 text-blue-500 mt-0.5" />
                    <span>End-to-end learning platform development and integration.</span>
                  </li>
                  <li className="flex gap-2">
                    <IconWrapper icon={HiBolt} className="w-4 h-4 text-blue-500 mt-0.5" />
                    <span>Student engagement tools and analytics dashboards.</span>
                  </li>
                  <li className="flex gap-2">
                    <IconWrapper icon={HiShieldCheck} className="w-4 h-4 text-blue-500 mt-0.5" />
                    <span>FERPA compliance and data security built-in.</span>
                  </li>
                </ul>
              </div>
              <motion.button
                className="mt-8 group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-slate-800 text-white font-semibold rounded-2xl shadow-lg overflow-hidden"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="relative z-10 flex items-center">
                  Schedule an education workshop
                  <IconWrapper icon={HiArrowRight} className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-slate-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EducationSolutionPage;

